import React, { useEffect, useState } from 'react';
import { Handshake, Plus, X, Trash2, ExternalLink, Zap, Eye, EyeOff, Loader } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const TYPES = [
  { value: 'assurance', label: 'Assurance' },
  { value: 'conventionne', label: 'Organisme conventionné' },
];

export default function AdminPartners({ token }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'assurance', url: '', order: 0 });
  const [extracting, setExtracting] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setPartners(data.partners || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const resetForm = () => {
    setForm({ name: '', type: 'assurance', url: '', order: 0 });
    setPreviewLogo(null);
    setManualFile(null);
    setError('');
    setShowForm(false);
  };

  const handleExtract = async () => {
    if (!form.url) return setError('Renseignez l\'URL du site d\'abord.');
    setExtracting(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/partners/extract-logo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: form.url }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Extraction impossible');
      setPreviewLogo({
        b64: data.logo_b64,
        type: data.content_type,
        size: data.size_kb,
        source: data.source_url,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setExtracting(false);
    }
  };

  const handleManualUpload = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return setError('Fichier trop volumineux (>3 MB)');
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(',')[1];
      setPreviewLogo({ b64, type: f.type, size: Math.round(f.size / 1024), source: 'upload manuel' });
      setManualFile(f);
    };
    reader.readAsDataURL(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.url) return setError('Nom et URL obligatoires.');
    if (!previewLogo) return setError('Ajoutez un logo (extraction auto ou upload manuel).');
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/partners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name, type: form.type, url: form.url, order: parseInt(form.order || 0),
          active: true, logo_b64: previewLogo.b64,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erreur');
      resetForm();
      await fetchList();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    await fetch(`${API}/api/admin/partners/${p.partner_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ active: !p.active }),
    });
    fetchList();
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer le partenaire "${p.name}" ?`)) return;
    await fetch(`${API}/api/admin/partners/${p.partner_id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchList();
  };

  const grouped = {
    assurance: partners.filter(p => p.type === 'assurance'),
    conventionne: partners.filter(p => p.type === 'conventionne'),
  };

  return (
    <div data-testid="admin-partners">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Handshake size={24} /> Gestion des partenaires
        </h2>
        <button className="btn-primary" onClick={() => setShowForm(true)} data-testid="admin-partners-add-btn">
          <Plus size={18} /> Ajouter un partenaire
        </button>
      </div>

      {showForm && (
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: 10, marginBottom: '1.5rem', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <h3>Nouveau partenaire</h3>
            <button className="icon-btn" onClick={resetForm}><X size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Nom *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Rawsur Assurances"
                required
                data-testid="partner-name-input"
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 10 }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Type *</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="partner-type-select"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1' }}
                >
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Ordre</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1' }}
                />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Site web *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://exemple.com"
                  required
                  data-testid="partner-url-input"
                  style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #cbd5e1' }}
                />
                <button type="button" onClick={handleExtract} disabled={extracting || !form.url}
                        data-testid="partner-extract-btn"
                        style={{ padding: '10px 14px', background: '#1565C0', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {extracting ? <Loader size={16} className="spin" /> : <Zap size={16} />}
                  {extracting ? 'Extraction...' : 'Extraire logo'}
                </button>
              </div>
              <p style={{ fontSize: 12, color: '#64748B', margin: '4px 0 0' }}>
                Cliquez sur "Extraire logo" pour tenter une récupération automatique depuis le site.
              </p>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>...ou uploadez manuellement</label>
              <input type="file" accept="image/*" onChange={handleManualUpload} data-testid="partner-manual-file" />
            </div>

            {previewLogo && (
              <div style={{ padding: 12, background: '#F8FBFF', borderRadius: 8, border: '1px solid #cfe0f5' }}>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#0D2C54', fontWeight: 600 }}>
                  Aperçu du logo ({previewLogo.size} KB, {previewLogo.type})
                </p>
                <img src={`data:${previewLogo.type};base64,${previewLogo.b64}`}
                     alt="Aperçu"
                     style={{ maxWidth: 200, maxHeight: 100, background: 'white', padding: 8, borderRadius: 6 }} />
              </div>
            )}

            {error && <div style={{ padding: 10, background: '#FEE2E2', color: '#B91C1C', borderRadius: 6, fontSize: 14 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
              <button type="submit" disabled={saving || !previewLogo} className="btn-primary" data-testid="partner-save-btn">
                {saving ? 'Enregistrement...' : 'Créer le partenaire'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <>
          {['assurance', 'conventionne'].map((type) => (
            <div key={type} style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.05rem', color: '#0D2C54', marginBottom: 12 }}>
                {type === 'assurance' ? 'Compagnies d\'assurance' : 'Organismes conventionnés'}
                {' '}<span style={{ color: '#94a3b8', fontWeight: 400 }}>({grouped[type].length})</span>
              </h3>
              {grouped[type].length === 0 ? (
                <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>Aucun partenaire dans cette catégorie.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {grouped[type].map((p) => (
                    <div key={p.partner_id} data-testid={`admin-partner-card-${p.partner_id}`}
                         style={{ background: 'white', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0',
                                  opacity: p.active ? 1 : 0.55, position: 'relative' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div style={{ width: 60, height: 60, background: '#F8FBFF', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          <img src={`${API}${p.logo_url}`} alt={p.name}
                               style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#0D2C54', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                          <a href={p.url} target="_blank" rel="noopener noreferrer"
                             style={{ fontSize: 12, color: '#1565C0', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <ExternalLink size={12} /> {p.url.replace(/^https?:\/\//, '').split('/')[0]}
                          </a>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                        <button onClick={() => toggleActive(p)}
                                title={p.active ? 'Masquer du site' : 'Rendre visible'}
                                data-testid={`partner-toggle-${p.partner_id}`}
                                style={{ padding: 6, background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                          {p.active ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Masqué</>}
                        </button>
                        <button onClick={() => handleDelete(p)}
                                title="Supprimer"
                                data-testid={`partner-delete-${p.partner_id}`}
                                style={{ padding: 6, background: 'transparent', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: 6, cursor: 'pointer' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
